// TODO (Noah, 2024-10-09): Re-enable this rule
import type { ReploElementMetadata } from "schemas/generated/element";
import type { ReploProject } from "schemas/generated/project";

import * as React from "react";

import { useAppLoading } from "@core/contexts/AppLoadingContext";
import { skipToken } from "@tanstack/react-query";
import WorkspaceProvider from "@v2/contexts/WorkspaceProvider";
import { adminCommandIcon } from "@v2/features/admin/adminCommandIcon";
import { initProjectBasedAnalytics } from "@v2/features/infra/analytics/init";
import useCurrentUser from "@v2/hooks/useCurrentUser";
import { useDynamicCommandMenuItem } from "@v2/hooks/useDynamicCommandMenuItems";
import { useIsDebugMode } from "@v2/hooks/useIsDebugMode";
import { elementRouter } from "@v2/trpc/routes/element";
import { projectRouter } from "@v2/trpc/routes/project";
import { subscriptionRouter } from "@v2/trpc/routes/subscription";
import { routes } from "@v2/utils/router";
import { getTRPCErrorStatus } from "@v2/utils/trpcErrorUtils";
import { Navigate } from "react-router-dom";
import {
  setLastVisitedDashboardRouteForProject,
  setLastVisitedProjectId,
} from "replo-utils/lib/lastVisitedDashboardIds";

type CurrentProjectContext = {
  project?: ReploProject;
  isLoading: boolean;
  // TODO (Fran 2024-08-29): improve this type
  // oxlint-disable-next-line typescript/no-explicit-any -- legacy any, may or may not be needed
  error: any;
  userRole?:
    | "superuser"
    | "workspaceOwner"
    | "workspaceMember"
    | "projectMember";
  projectId: string | undefined;
  elementMetadata?: ReploElementMetadata[];
  isElementMetadataSuccess: boolean;
};

const CurrentProjectContext = React.createContext<CurrentProjectContext>({
  project: undefined,
  isLoading: false,
  error: undefined,
  userRole: undefined,
  projectId: undefined,
  elementMetadata: undefined,
  isElementMetadataSuccess: false,
});

const DebugCommandMenuProjectItemsProvider = () => {
  const isDebugMode = useIsDebugMode();
  const projectContext = useCurrentProjectContext();
  // Open Project in Admin
  const openShopInAdminItem = React.useMemo(() => {
    if (!isDebugMode || !projectContext?.project) {
      return null;
    }

    return {
      label: "Open Project in Admin",
      description: `Open project ${projectContext.project.id} in Admin`,
      group: "debug" as const,
      icon: adminCommandIcon,
      onSelect: () => {
        window.open(
          `https://admin.replo.app/dashboard/shops?projectId=${projectContext?.project?.id}`,
          "_blank",
        );
      },
    };
  }, [isDebugMode, projectContext?.project]);
  useDynamicCommandMenuItem("debug-open-shop-admin", openShopInAdminItem);

  // Copy Shop ID
  const copyShopIdItem = React.useMemo(() => {
    if (!isDebugMode || !projectContext?.project) {
      return null;
    }

    return {
      label: "Copy Shop ID",
      description: `Copy ${projectContext.project.id}`,
      group: "debug" as const,
      icon: adminCommandIcon,
      onSelect: () => {
        void navigator.clipboard.writeText(projectContext.project!.id);
      },
    };
  }, [isDebugMode, projectContext?.project]);
  useDynamicCommandMenuItem("debug-copy-shop-id", copyShopIdItem);

  return null;
};

function getProductSurface({
  hostingType,
  agentEnabledAt,
}: {
  hostingType: ReploProject["hostingType"];
  agentEnabledAt: ReploProject["agentEnabledAt"];
}): "theme-builder" | "v2-ai" | "agent" {
  if (agentEnabledAt) {
    return "agent";
  }
  return hostingType === "shopify" ? "theme-builder" : "v2-ai";
}

export const useCurrentProjectContext = () => {
  return React.useContext(CurrentProjectContext);
};

export const useIsAgentEnabled = () => {
  const { project } = useCurrentProjectContext();
  return Boolean(project?.agentEnabledAt);
};

const CurrentProjectProvider: React.FC<
  React.PropsWithChildren<{
    projectId: string;
    saveProjectIdAsLastVisited: boolean;
    isAgentRoute?: boolean;
  }>
> = ({ children, projectId, saveProjectIdAsLastVisited }) => {
  const {
    project,
    error,
    isLoading,
    userRole,
    elementMetadata,
    isElementMetadataSuccess,
  } = useGetProjectQuery(projectId ?? null);

  const status = getTRPCErrorStatus(error);
  const isNoAccessError = status === 403;

  // eslint-disable-next-line replo/no-use-effect -- Persist route preferences when the selected project id and surface become known (REPL-30512, REPL-31104)
  React.useEffect(() => {
    if (!saveProjectIdAsLastVisited || !projectId) {
      return;
    }
    setLastVisitedProjectId(projectId);
    if (!project || project.id !== projectId) {
      return;
    }
    setLastVisitedDashboardRouteForProject({
      projectId: project.id,
      hostingType: project.hostingType,
      agentEnabledAt: project.agentEnabledAt ?? null,
    });
  }, [project, projectId, saveProjectIdAsLastVisited]);

  // Initialize project-based analytics
  const { user } = useCurrentUser();
  const subscriptionQueryArg =
    isNoAccessError || !project?.ownerWorkspaceId
      ? skipToken
      : { workspaceId: project.ownerWorkspaceId, projectId: project.id };

  const { data: subscriptionDetails } =
    subscriptionRouter.getActiveSubscription.useQuery(subscriptionQueryArg);

  // eslint-disable-next-line replo/no-use-effect -- Legacy effect - may or may not be necessary
  React.useEffect(() => {
    if (project && user) {
      initProjectBasedAnalytics(project, subscriptionDetails ?? undefined);
    }
  }, [project, user, subscriptionDetails]);

  useAppLoading("project", isLoading);

  // Note (Noah, 2026-01-14): Redirect to the no-access error page instead of showing
  // an inline error screen
  if (isNoAccessError) {
    return <Navigate to={routes.error.noAccess} replace />;
  }

  if (status === 404 || (error && status !== 403)) {
    return <Navigate to={routes.home.root} replace />;
  }

  if (isLoading) {
    return null;
  }

  return (
    <WorkspaceProvider
      workspaceId={project?.ownerWorkspaceId}
      saveWorkspaceIdAsLastVisited
    >
      <CurrentProjectContext.Provider
        value={{
          project,
          error,
          isLoading,
          userRole,
          projectId,
          elementMetadata,
          isElementMetadataSuccess,
        }}
      >
        {children}
        <DebugCommandMenuProjectItemsProvider />
      </CurrentProjectContext.Provider>
    </WorkspaceProvider>
  );
};
export default CurrentProjectProvider;

function useGetProjectQuery(projectId: string | null) {
  // Note (Ben O. 2025-02-25): We use refetchOnMount: "always" because when a users goes from project 1 -> dashboard -> project 1,
  // the projectId does not change so we do not invalidate these queries but the projectData is stale.
  const {
    data,
    isLoading: isProjectLoading,
    error,
    refetch,
  } = projectRouter.getV2.useQuery(projectId ?? skipToken, {
    refetchOnMount: "always",
  });

  const projectData = data?.project;
  const userRole = data?.userRole;

  const {
    data: elementMetadata,
    isLoading: isElementMetadataLoading,
    isSuccess: isElementMetadataSuccess,
  } = elementRouter.listAllElementsMetadata.useQuery(
    projectId ? { projectId } : skipToken,
    {
      refetchOnMount: "always",
    },
  );

  const isLoading = isProjectLoading || isElementMetadataLoading;

  // eslint-disable-next-line replo/no-use-effect -- Legacy effect - may or may not be necessary
  React.useEffect(() => {
    if (projectData?.hostingType) {
      window.Pylon?.("setNewIssueCustomFields", {
        project_hosting_type: projectData.hostingType,
        product_surface: getProductSurface({
          hostingType: projectData.hostingType,
          agentEnabledAt: projectData.agentEnabledAt,
        }),
      });
    }
  }, [projectData?.hostingType, projectData?.agentEnabledAt]);

  // eslint-disable-next-line replo/no-use-effect -- Legacy effect - may or may not be necessary
  React.useEffect(() => {
    // Legacy cast - may or may not be necessary
    // oxlint-disable-next-line replo/no-as-cast, typescript/no-explicit-any -- legacy any/cast, may or may not be needed
    if ((error as any)?.error?.status === 401) {
      window.location.href = "/auth/login";
    }
  }, [error]);

  return {
    project: projectData,
    refetch,
    isLoading,
    error,
    userRole,
    elementMetadata,
    isElementMetadataSuccess,
  };
}
